import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { TextLink } from '../../../components/TextLink';
import { currentUserQuery, useUpdateProfileMutation, type PartnerCockpitUser } from '../../../lib/auth';
import { useCompanyProfile } from '../api/profileApi';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsQueryGate } from '../components/SettingsQueryGate';
import { settingsTabPath } from '../routes';
import { useDirtyForm } from '../state/useDirtyForm';

type NotificationsPageData = {
    user: Pick<PartnerCockpitUser, 'smsNotificationsEnabled' | 'phoneNumber'>;
    smsDisabledByAccount: boolean;
};

export function NotificationsSettingsPage(): ReactElement {
    const userQuery = useQuery(currentUserQuery);
    const profileQuery = useCompanyProfile();

    const combinedQuery: Pick<UseQueryResult<NotificationsPageData>, 'isError' | 'data'> = {
        isError: userQuery.isError || profileQuery.isError,
        data:
            userQuery.data && profileQuery.data
                ? { user: userQuery.data, smsDisabledByAccount: profileQuery.data.smsDisabledByAccount }
                : undefined,
    };

    return (
        <SettingsQueryGate
            query={combinedQuery}
            loading={
                <Stack spacing={{ xs: 2, md: 3 }} aria-busy>
                    <Skeleton variant="rounded" height={200} />
                </Stack>
            }>
            {({ user, smsDisabledByAccount }, isStale) => (
                <NotificationsForm user={user} smsDisabledByAccount={smsDisabledByAccount} isStale={isStale} />
            )}
        </SettingsQueryGate>
    );
}

type NotificationsFormProps = NotificationsPageData & { isStale: boolean };

type NotificationsFormValue = { smsNotificationsEnabled: boolean };

function NotificationsForm({ user, smsDisabledByAccount, isStale }: NotificationsFormProps): ReactElement {
    const { t } = useTranslation();
    const updateProfile = useUpdateProfileMutation();
    const { value, setValue, reset, isDirty } = useDirtyForm<NotificationsFormValue>({
        smsNotificationsEnabled: user.smsNotificationsEnabled,
    });

    return (
        <Stack spacing={{ xs: 2, md: 3 }}>
            <SettingsCard
                title={t('SETTINGS.NOTIFICATIONS.TITLE')}
                dirty={isDirty}
                saving={updateProfile.isPending}
                disableSave={isStale}
                onSave={() => updateProfile.mutate({ smsNotificationsEnabled: value.smsNotificationsEnabled })}
                onReset={reset}>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
                        <Stack spacing={0.5} sx={{ pr: 2 }}>
                            <Typography variant="body1">{t('SETTINGS.NOTIFICATIONS.SMS.TITLE')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('SETTINGS.NOTIFICATIONS.SMS.DESCRIPTION')}
                            </Typography>
                        </Stack>
                        <Switch
                            checked={value.smsNotificationsEnabled}
                            disabled={smsDisabledByAccount}
                            onChange={(e) => setValue({ smsNotificationsEnabled: e.target.checked })}
                            slotProps={{ input: { 'aria-label': t('SETTINGS.NOTIFICATIONS.SMS.ARIA_LABEL') } }}
                        />
                    </Stack>
                    {smsDisabledByAccount ? (
                        <Alert severity="warning" variant="outlined">
                            <AlertTitle>{t('SETTINGS.NOTIFICATIONS.SMS.DISABLED_BY_ACCOUNT_TITLE')}</AlertTitle>
                            <Typography variant="body2">
                                {t('SETTINGS.NOTIFICATIONS.SMS.DISABLED_BY_ACCOUNT_BODY_PREFIX')}{' '}
                                <TextLink to={settingsTabPath('support')} color="inherit">
                                    {t('SETTINGS.TABS.SUPPORT')}
                                </TextLink>{' '}
                                {t('SETTINGS.NOTIFICATIONS.SMS.DISABLED_BY_ACCOUNT_BODY_SUFFIX')}
                            </Typography>
                        </Alert>
                    ) : (
                        value.smsNotificationsEnabled && (
                            <Typography variant="body2" color="text.secondary">
                                {user.phoneNumber
                                    ? t('SETTINGS.NOTIFICATIONS.SMS.PHONE_INFO', { phoneNumber: user.phoneNumber })
                                    : t('SETTINGS.NOTIFICATIONS.SMS.PHONE_MISSING')}
                            </Typography>
                        )
                    )}
                </Stack>
            </SettingsCard>
        </Stack>
    );
}
